import { useState, useEffect } from "react";
import api from "../api";
import Modal from "../components/Modal";
import { generateSalarySlipDoc } from "../salarySlipPdf";
import { useAuth } from "../auth";

function salaryMonthOptions() {
  const opts = [];
  const now = new Date();
  for (let i = -3; i <= 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const label = d.toLocaleString("en", {month:"short"}) + ", " + d.getFullYear();
    opts.push(label);
  }
  return opts;
}
function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
}
function formatSlipDate(isoDate) {
  if (!isoDate) return "";
  const d = new Date(isoDate + "T00:00:00");
  const day = String(d.getDate()).padStart(2,"0");
  const mon = d.toLocaleString("en", {month:"short"});
  return `${day}-${mon}-${d.getFullYear()}`;
}

export default function Salary() {
  const { anyAdmin } = useAuth();
  const isAdmin = anyAdmin();
  const [tab, setTab] = useState(isAdmin ? "create" : "mine");

  return (
    <div>
      <h2 className="page-title">Salary Slips</h2>
      <p className="page-sub">{isAdmin ? "Create and manage salary slips for your team" : "Your salary slip history"}</p>
      {isAdmin && (
        <div className="tabs" style={{marginTop:14}}>
          {[["create","Create Batch"],["upload","Upload Excel"],["history","History"],["email","Email Settings"]].map(([id,label]) => (
            <button key={id} className={`tab ${tab===id?"active":""}`} onClick={()=>setTab(id)}>{label}</button>
          ))}
        </div>
      )}
      {isAdmin && tab === "create"  && <CreateBatchTab/>}
      {isAdmin && tab === "upload"  && <UploadExcelTab/>}
      {isAdmin && tab === "history" && <HistoryTab/>}
      {isAdmin && tab === "email"   && <EmailSettingsTab/>}
      {!isAdmin && <MySlipsTab/>}
    </div>
  );
}

function useEmployeeList() {
  const [employees, setEmployees] = useState([]);
  useEffect(() => { api.get("/portal/admin/users").then(d => setEmployees(d.users || [])).catch(()=>{}); }, []);
  return employees;
}

function buildPdfPayload(r, batchMeta) {
  return {
    empId: r.employee_id, name: r.emp_name, designation: r.designation,
    department: r.department, dateOfJoining: r.date_of_joining,
    basic: r.basic, allowances: r.allowances, bonus: r.bonus,
    deductions: r.deductions, netPay: r.net_pay,
    salaryMonth: batchMeta.salary_month, slipDate: batchMeta.slip_date,
    preparedBy: batchMeta.prepared_by, approvedBy: batchMeta.approved_by,
  };
}

async function downloadSlip(r, batchMeta) {
  const doc = await generateSalarySlipDoc(buildPdfPayload(r, batchMeta));
  doc.save(`${r.employee_id}_${r.emp_name.replace(/\s+/g,"_")}_SalarySlip.pdf`);
}

// ── Admin: Create Batch (manual entry) ──────────────────────────────────
function CreateBatchTab() {
  const employees = useEmployeeList();
  const [salaryMonth, setSalaryMonth] = useState(salaryMonthOptions()[3]); // current month
  const [slipDateIso, setSlipDateIso] = useState(todayISO());
  const [preparedBy, setPreparedBy] = useState("IWS Finance Team");
  const [approvedBy, setApprovedBy] = useState("Aashish Kumar Goyal");
  const [rows, setRows] = useState([]);
  const [msg, setMsg] = useState("");

  const rowFromEmployee = (emp) => ({
    employee_id: emp.employee_id, name: emp.name,
    designation: emp.designation || "", department: emp.department || "",
    date_of_joining: emp.date_of_joining || "",
    basic: 0, allowances: 0, bonus: 0, deductions: 0,
  });

  const addEmployee = (empId) => {
    if (!empId || rows.find(r=>r.employee_id===empId)) return;
    const emp = employees.find(e=>e.employee_id===empId);
    if (!emp) return;
    setRows([...rows, rowFromEmployee(emp)]);
  };
  const addAll = () => {
    const missing = employees.filter(e=>!rows.find(r=>r.employee_id===e.employee_id));
    setRows([...rows, ...missing.map(rowFromEmployee)]);
  };
  const updateRow = (idx, field, value) => {
    const next = [...rows];
    next[idx] = {...next[idx], [field]: value};
    setRows(next);
  };
  const removeRow = (idx) => setRows(rows.filter((_,i)=>i!==idx));
  const netPay = r => (parseFloat(r.basic)||0) + (parseFloat(r.allowances)||0) + (parseFloat(r.bonus)||0) - (parseFloat(r.deductions)||0);

  const save = async () => {
    if (!salaryMonth.trim()) { setMsg("Salary month is required (e.g. Aug, 2025)."); return; }
    if (!rows.length) { setMsg("Add at least one employee."); return; }
    try {
      const res = await api.post("/admin/salary/batches", {
        salary_month: salaryMonth, slip_date: formatSlipDate(slipDateIso),
        prepared_by: preparedBy, approved_by: approvedBy,
        records: rows.map(r => ({
          employee_id: r.employee_id, designation: r.designation, department: r.department,
          date_of_joining: r.date_of_joining,
          basic: parseFloat(r.basic)||0, allowances: parseFloat(r.allowances)||0,
          bonus: parseFloat(r.bonus)||0, deductions: parseFloat(r.deductions)||0,
        })),
      });
      setMsg(`✓ Batch created with ${res.records.length} slip(s).`);
      setRows([]); setPreparedBy("IWS Finance Team"); setApprovedBy("Aashish Kumar Goyal");
    } catch(e) { setMsg(e.message); }
  };

  return (
    <div style={{marginTop:16}}>
      <div className="card" style={{marginBottom:16}}>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
          <div><label>Salary Month *</label>
            <select className="inp" value={salaryMonth} onChange={e=>setSalaryMonth(e.target.value)}>
              {salaryMonthOptions().map(m=><option key={m} value={m}>{m}</option>)}
            </select></div>
          <div><label>Slip Date</label>
            <input className="inp" type="date" value={slipDateIso} onChange={e=>setSlipDateIso(e.target.value)}/></div>
          <div><label>Prepared By (optional)</label>
            <input className="inp" value={preparedBy} onChange={e=>setPreparedBy(e.target.value)} placeholder="IWS Finance Team"/></div>
          <div><label>Approved By / Digitally Signed By (optional)</label>
            <input className="inp" value={approvedBy} onChange={e=>setApprovedBy(e.target.value)} placeholder="Aashish Kumar Goyal"/></div>
        </div>
      </div>

      <div style={{display:"flex",gap:8,marginBottom:10,alignItems:"center"}}>
        <select className="inp" style={{width:"auto",flex:1}} onChange={e=>{addEmployee(e.target.value); e.target.value="";}}>
          <option value="">+ Add employee…</option>
          {employees.filter(e=>!rows.find(r=>r.employee_id===e.employee_id)).map(e=>
            <option key={e.employee_id} value={e.employee_id}>{e.name} — {e.employee_id}</option>)}
        </select>
        <button className="btn small" onClick={addAll}>+ Add All</button>
      </div>

      {rows.length > 0 && (
        <div className="card" style={{overflowX:"auto",marginBottom:16}}>
          <table className="tbl">
            <thead><tr>
              <th>Employee</th><th>Designation</th><th>Department</th>
              <th>Basic</th><th>Allowances</th><th>Bonus</th><th>Deductions</th><th>Net Pay</th><th></th>
            </tr></thead>
            <tbody>
              {rows.map((r,i)=>(
                <tr key={r.employee_id}>
                  <td><b>{r.name}</b><div style={{fontSize:11,color:"var(--muted)"}}>{r.employee_id}</div></td>
                  <td><input className="inp" style={{width:110}} value={r.designation} onChange={e=>updateRow(i,"designation",e.target.value)}/></td>
                  <td><input className="inp" style={{width:110}} value={r.department} onChange={e=>updateRow(i,"department",e.target.value)}/></td>
                  <td><input className="inp" type="number" style={{width:90}} value={r.basic} onChange={e=>updateRow(i,"basic",e.target.value)}/></td>
                  <td><input className="inp" type="number" style={{width:90}} value={r.allowances} onChange={e=>updateRow(i,"allowances",e.target.value)}/></td>
                  <td><input className="inp" type="number" style={{width:90}} value={r.bonus} onChange={e=>updateRow(i,"bonus",e.target.value)}/></td>
                  <td><input className="inp" type="number" style={{width:90}} value={r.deductions} onChange={e=>updateRow(i,"deductions",e.target.value)}/></td>
                  <td><b>{netPay(r).toFixed(2)}</b></td>
                  <td><button onClick={()=>removeRow(i)} style={{background:"none",border:"none",color:"#ef4444",cursor:"pointer"}}>✕</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {rows.length > 0 && (
        <button className="btn" onClick={save}>Generate & Save Batch ({rows.length} slip{rows.length!==1?"s":""})</button>
      )}
      {msg && <div style={{marginTop:10,color:msg.startsWith("✓")?"var(--ok)":"var(--danger)"}}>{msg}</div>}
    </div>
  );
}

// ── Admin: Upload Excel ───────────────────────────────────────────────────
function UploadExcelTab() {
  const [file, setFile] = useState(null);
  const [salaryMonth, setSalaryMonth] = useState(salaryMonthOptions()[3]);
  const [slipDateIso, setSlipDateIso] = useState(todayISO());
  const [preparedBy, setPreparedBy] = useState("IWS Finance Team");
  const [approvedBy, setApprovedBy] = useState("Aashish Kumar Goyal");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  const upload = async () => {
    if (!salaryMonth.trim()) { setMsg("Salary Month is required (e.g. Aug, 2025)."); return; }
    if (!file) { setMsg("Choose a .xlsx file first."); return; }
    setBusy(true); setMsg("");
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("salary_month", salaryMonth);
      form.append("slip_date", formatSlipDate(slipDateIso));
      form.append("prepared_by", preparedBy);
      form.append("approved_by", approvedBy);
      const res = await api.post("/admin/salary/batches/upload_excel", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      let out = `\u2713 Batch created \u2014 ${res.created_count} slip(s) generated.`;
      if (res.unlinked_ids?.length) out += ` (${res.unlinked_ids.length} not yet linked to a portal account: ${res.unlinked_ids.join(", ")} -- will appear automatically once those accounts are created.)`;
      out += " Go to History to review and Send them to employees.";
      setMsg(out);
      setFile(null);
    } catch(e) { setMsg(e.message); }
    setBusy(false);
  };

  return (
    <div style={{marginTop:16}} className="card">
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:16}}>
        <div><label>Salary Month *</label>
          <select className="inp" value={salaryMonth} onChange={e=>setSalaryMonth(e.target.value)}>
            {salaryMonthOptions().map(m=><option key={m} value={m}>{m}</option>)}
          </select></div>
        <div><label>Slip Date</label>
          <input className="inp" type="date" value={slipDateIso} onChange={e=>setSlipDateIso(e.target.value)}/></div>
        <div><label>Prepared By (optional)</label>
          <input className="inp" value={preparedBy} onChange={e=>setPreparedBy(e.target.value)} placeholder="IWS Finance Team"/></div>
        <div><label>Approved By / Digitally Signed By (optional)</label>
          <input className="inp" value={approvedBy} onChange={e=>setApprovedBy(e.target.value)} placeholder="Aashish Kumar Goyal"/></div>
      </div>
      <p style={{fontSize:13,color:"var(--muted)",marginBottom:14}}>
        Upload a workbook with an <b>emp_details</b> sheet (header row: Employee ID, Name, Designation,
        Department, Basic Salary, Allowances, Deduction, Net Salary Amount). The details above apply
        to the whole batch.
      </p>
      <input type="file" accept=".xlsx,.xls" onChange={e=>setFile(e.target.files[0])}/>
      <div style={{marginTop:14}}>
        <button className="btn" onClick={upload} disabled={busy}>{busy ? "Uploading\u2026" : "Upload & Generate"}</button>
      </div>
      {msg && <div style={{marginTop:10,color:msg.startsWith("\u2713")?"var(--ok)":"var(--danger)"}}>{msg}</div>}
    </div>
  );
}

// ── Admin: History ────────────────────────────────────────────────────────
function HistoryTab() {
  const [batches, setBatches] = useState([]);
  const [selected, setSelected] = useState(null);
  const [checked, setChecked] = useState({});
  const load = () => api.get("/admin/salary/batches").then(d=>setBatches(d.items||[]));
  useEffect(() => { load(); }, []);

  const openBatch = (id) => {
    api.get(`/admin/salary/batches/${id}`).then(d => { setSelected(d); setChecked({}); });
  };
  const deleteBatch = async (id) => {
    if (!confirm("Delete this batch and all its slips?")) return;
    await api.delete(`/admin/salary/batches/${id}`);
    setSelected(null); load();
  };

  const toggleOne = (id) => setChecked(c => ({...c, [id]: !c[id]}));
  const toggleAll = () => {
    const allChecked = selected.records.every(r => checked[r.id]);
    const next = {};
    if (!allChecked) selected.records.forEach(r => { next[r.id] = true; });
    setChecked(next);
  };
  const selectedIds = () => Object.keys(checked).filter(id => checked[id]).map(Number);

  const sendOne = async (id) => {
    await api.post(`/admin/salary/records/${id}/send`, {});
    openBatch(selected.id);
  };
  const sendSelected = async () => {
    const ids = selectedIds();
    if (!ids.length) { alert("Select at least one row."); return; }
    if (!confirm(`Send ${ids.length} slip(s) to employees? They will become visible in each employee's portal.`)) return;
    await api.post("/admin/salary/records/send_bulk", { record_ids: ids });
    openBatch(selected.id);
  };
  const deleteOne = async (id, name) => {
    if (!confirm(`Delete ${name}'s slip? This also removes it from their portal immediately.`)) return;
    await api.delete(`/admin/salary/records/${id}`);
    openBatch(selected.id);
  };

  return (
    <div style={{marginTop:16,display:"grid",gridTemplateColumns:"260px 1fr",gap:16}}>
      <div className="card" style={{padding:0,overflow:"hidden"}}>
        {batches.map(b=>(
          <div key={b.id} onClick={()=>openBatch(b.id)}
            style={{padding:"12px 14px",borderBottom:"1px solid var(--border,#eee)",cursor:"pointer",
                    background:selected?.id===b.id?"var(--bg-soft,#f0fdf9)":"transparent"}}>
            <b>{b.salary_month}</b>
            <div style={{fontSize:12,color:"var(--muted)"}}>{b.record_count} slip(s)</div>
          </div>
        ))}
        {!batches.length && <div style={{padding:14,color:"var(--muted)",fontSize:13}}>No batches yet.</div>}
      </div>
      <div>
        {!selected && <div className="card" style={{textAlign:"center",color:"var(--muted)"}}>Select a batch to view its slips.</div>}
        {selected && (
          <div className="card" style={{overflowX:"auto"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10,flexWrap:"wrap",gap:8}}>
              <b>{selected.salary_month}</b>
              <div style={{display:"flex",gap:8}}>
                <button className="btn small" onClick={sendSelected}>Send Selected ({selectedIds().length})</button>
                <button className="btn small danger" onClick={()=>deleteBatch(selected.id)}>Delete Batch</button>
              </div>
            </div>
            <table className="tbl">
              <thead><tr>
                <th><input type="checkbox"
                  checked={selected.records.length>0 && selected.records.every(r=>checked[r.id])}
                  onChange={toggleAll}/></th>
                <th>Employee</th><th>Designation</th><th>Net Pay</th><th>Status</th><th></th>
              </tr></thead>
              <tbody>
                {selected.records.map(r=>(
                  <tr key={r.id}>
                    <td><input type="checkbox" checked={!!checked[r.id]} onChange={()=>toggleOne(r.id)}/></td>
                    <td>
                      <b>{r.emp_name}</b>
                      <div style={{fontSize:11,color:"var(--muted)"}}>
                        {r.employee_id}{!r.has_account && <span style={{color:"#f59e0b"}}> (no portal account yet)</span>}
                      </div>
                    </td>
                    <td>{r.designation||"—"}</td>
                    <td><b>{r.net_pay}</b></td>
                    <td>{r.is_sent
                      ? <span className="pill green" style={{fontSize:10}}>Sent</span>
                      : <span className="pill gray" style={{fontSize:10}}>Draft</span>}</td>
                    <td style={{display:"flex",gap:6}}>
                      <button className="btn small" onClick={()=>downloadSlip(r, selected)}>Download PDF</button>
                      {!r.is_sent && <button className="btn small" onClick={()=>sendOne(r.id)}>Send</button>}
                      <button className="btn small danger" onClick={()=>deleteOne(r.id, r.emp_name)}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function MySlipsTab() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/portal/salary/my_slips").then(d=>setItems(d.items||[])); }, []);

  const download = (r) => downloadSlip(r, {
    salary_month: r.salary_month, slip_date: r.slip_date,
    prepared_by: r.prepared_by, approved_by: r.approved_by,
  });

  return (
    <div style={{marginTop:16,display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(240px,1fr))",gap:12}}>
      {items.map(r=>(
        <div key={r.id} className="card">
          <div style={{fontWeight:700,fontSize:15,marginBottom:6}}>{r.salary_month}</div>
          <div style={{fontSize:24,fontWeight:700,color:"var(--accent)"}}>₹{r.net_pay.toLocaleString("en-IN")}</div>
          <div style={{fontSize:12,color:"var(--muted)",marginBottom:12}}>Net Pay</div>
          <button className="btn small full" onClick={()=>download(r)}>Download PDF</button>
        </div>
      ))}
      {!items.length && <div className="card" style={{gridColumn:"1/-1",textAlign:"center",color:"var(--muted)"}}>No salary slips yet. Your admin will generate these once payroll is processed.</div>}
    </div>
  );
}


// ── Admin: Email Settings (SMTP for salary slip notifications) ──────────
function EmailSettingsTab() {
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({
    smtp_host:"smtp.gmail.com", smtp_port:587, smtp_user:"",
    smtp_password:"", from_name:"Invincible Web Solutions", use_tls:true,
  });
  const [testTo, setTestTo] = useState("");
  const [msg, setMsg] = useState("");
  const [testMsg, setTestMsg] = useState("");

  const load = () => api.get("/admin/email_settings").then(d => {
    setSettings(d);
    setForm(f => ({...f, smtp_host:d.smtp_host, smtp_port:d.smtp_port,
      smtp_user:d.smtp_user, from_name:d.from_name, use_tls:d.use_tls, smtp_password:""}));
  });
  useEffect(() => { load(); }, []);

  const save = async () => {
    setMsg("");
    try {
      await api.post("/admin/email_settings", form);
      setMsg("✓ Settings saved.");
      setForm(f => ({...f, smtp_password:""}));
      load();
    } catch(e) { setMsg(e.message); }
  };

  const sendTest = async () => {
    setTestMsg("Sending…");
    try {
      const res = await api.post("/admin/email_settings/test", { to_email: testTo });
      setTestMsg(`✓ Test email sent to ${res.sent_to}`);
    } catch(e) { setTestMsg(e.message); }
  };

  if (!settings) return <div style={{marginTop:16,color:"var(--muted)"}}>Loading…</div>;

  return (
    <div style={{marginTop:16,display:"grid",gap:16,maxWidth:600}}>
      <div className="card">
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:14}}>
          <span className={`pill ${settings.configured?"green":"orange"}`} style={{fontSize:11}}>
            {settings.configured ? "Configured" : "Not configured"}
          </span>
          <span style={{fontSize:12,color:"var(--muted)"}}>
            Used to notify employees by email when their salary slip is sent.
          </span>
        </div>
        <div style={{display:"grid",gap:12}}>
          <div><label>SMTP Host</label>
            <input className="inp" value={form.smtp_host} onChange={e=>setForm({...form,smtp_host:e.target.value})}/></div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
            <div><label>SMTP Port</label>
              <input className="inp" type="number" value={form.smtp_port} onChange={e=>setForm({...form,smtp_port:e.target.value})}/></div>
            <div style={{display:"flex",alignItems:"flex-end",paddingBottom:10}}>
              <label style={{display:"flex",gap:6,alignItems:"center",fontWeight:400}}>
                <input type="checkbox" checked={form.use_tls} onChange={e=>setForm({...form,use_tls:e.target.checked})}/>
                Use TLS
              </label>
            </div>
          </div>
          <div><label>Sender Email (e.g. hello@invincibleweb.in)</label>
            <input className="inp" value={form.smtp_user} onChange={e=>setForm({...form,smtp_user:e.target.value})}/></div>
          <div><label>From Name</label>
            <input className="inp" value={form.from_name} onChange={e=>setForm({...form,from_name:e.target.value})}/></div>
          <div>
            <label>App Password {settings.has_password && <span style={{color:"var(--muted)",fontWeight:400}}>(already set — leave blank to keep it)</span>}</label>
            <input className="inp" type="password" value={form.smtp_password}
              placeholder={settings.has_password ? "•••••••••••••••• (unchanged)" : "16-character Google App Password"}
              onChange={e=>setForm({...form,smtp_password:e.target.value})}/>
            <div style={{fontSize:12,color:"var(--muted)",marginTop:4}}>
              For Gmail/Google Workspace: myaccount.google.com → Security → App Passwords
              (requires 2-Step Verification enabled on that account).
            </div>
          </div>
          <button className="btn" onClick={save}>Save Settings</button>
          {msg && <div style={{color:msg.startsWith("✓")?"var(--ok)":"var(--danger)"}}>{msg}</div>}
        </div>
      </div>

      <div className="card">
        <div style={{fontWeight:700,marginBottom:10}}>Send a Test Email</div>
        <div style={{display:"flex",gap:8}}>
          <input className="inp" placeholder="Send test to… (defaults to your own email)"
            value={testTo} onChange={e=>setTestTo(e.target.value)}/>
          <button className="btn small" onClick={sendTest}>Send Test</button>
        </div>
        {testMsg && <div style={{marginTop:8,color:testMsg.startsWith("✓")?"var(--ok)":"var(--danger)"}}>{testMsg}</div>}
      </div>
    </div>
  );
}
